import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  FolderKanban,
  Plane,
  FileText,
  Users,
  MessagesSquare,
  LogIn,
  ImageOff,
  ArrowUpRight,
  ShieldCheck,
} from "lucide-react";
import Reveal from "@/components/landing/Reveal";
import { portalNotice } from "@/components/landing/Navbar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  {
    id: "projektverwaltung",
    icon: FolderKanban,
    title: "Projektverwaltung",
    text: "Jedes Bauwerk, jede Anlage und jeder Auftrag zentral gebündelt — Status, Beteiligte und Fortschritt jederzeit im Blick.",
  },
  {
    id: "einsatzverwaltung",
    icon: Plane,
    title: "Einsatzverwaltung",
    text: "Missionen planen, Flugfenster vergeben und Einsätze koordinieren — von der Anfrage bis zum Abschluss in einem System.",
  },
  {
    id: "dokumentation",
    icon: FileText,
    title: "Digitale Dokumentation & Berichte",
    text: "Aufnahmen, Flugdaten und Befunde laufen automatisch zusammen — erfasst und aufbereitet mit FIELD-REPORT-11.",
  },
  {
    id: "team-koordination",
    icon: Users,
    title: "Team- & Personalkoordination",
    text: "Verfügbarkeiten, Rollen und Zuständigkeiten zentral steuern — das richtige Team zur richtigen Zeit am richtigen Ort.",
  },
  {
    id: "kundenportal",
    icon: MessagesSquare,
    title: "Kundenportal & Live-Chat",
    text: "Kunden verfolgen Anfragen, Termine und Dokumente im geschützten Portal — inklusive direktem Live-Chat mit dem Team.",
  },
];

export default function Connect() {
  const [shots, setShots] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    axios
      .get(`${BACKEND}/api/public/site-images?slot=app-screenshot`)
      .then((r) => setShots(r.data || []))
      .catch(() => {});
  }, []);

  const current = shots[active];

  return (
    <section id="connect" data-testid="connect-section" className="relative border-b border-white/10">
      <div className="pointer-events-none absolute inset-0 z-0 opacity-[0.05] [background-image:linear-gradient(#F5B301_1px,transparent_1px),linear-gradient(90deg,#F5B301_1px,transparent_1px)] [background-size:52px_52px]" />

      <div className="relative z-10 px-6 py-20 lg:px-16 lg:py-28">
        {/* HEADER */}
        <Reveal>
          <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">DAS SYSTEM DAHINTER</p>
          <h2 className="mt-6 max-w-4xl font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-5xl lg:text-6xl">
            Drops <span className="text-[#F5B301]">Connect.</span>
          </h2>
          <p className="mt-7 max-w-2xl font-tac text-sm leading-relaxed text-[#A1A1AA]">
            Unser digitales Einsatzsystem: Projekte, Missionen, Dokumentation, Team und Kunden laufen in einer
            Plattform zusammen — offline-fähig, einsatzfest und ohne Zettelwirtschaft. Vom ersten Auftrag bis zum
            fertigen, revisionssicheren Bericht.
          </p>
        </Reveal>

        {/* FEATURES + SCREENSHOT */}
        <div className="mt-14 grid gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Feature list */}
          <div className="lg:col-span-5">
            <div className="grid border-l border-t border-white/10">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <Reveal key={f.id} delay={i * 0.06} className="border-b border-r border-white/10">
                    <div
                      data-testid={`connect-feature-${f.id}`}
                      className="group flex h-full items-start gap-4 p-6 transition-colors duration-300 hover:bg-white/[0.03]"
                    >
                      <Icon className="mt-1 h-6 w-6 shrink-0 text-[#F5B301]/80 transition-colors group-hover:text-[#F5B301]" strokeWidth={1.5} />
                      <div>
                        <h3 className="font-display text-lg font-black uppercase tracking-tight text-white">{f.title}</h3>
                        <p className="mt-2 font-tac text-xs leading-relaxed text-[#A1A1AA]">{f.text}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          {/* Screenshot showcase */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="relative">
                <p className="font-tac text-[11px] tracking-[0.3em] text-white/40">EINBLICKE IN DROPS CONNECT</p>

                {shots.length > 0 ? (
                  <div className="mt-4" data-testid="connect-screenshots">
                    <div className="relative overflow-hidden border border-white/15 bg-black/40">
                      <img
                        key={current?.id}
                        data-testid="connect-screenshot-active"
                        src={`${BACKEND}${current.url}`}
                        alt={current.alt || current.title || "DROPS Connect App-Screenshot"}
                        className="w-full object-contain"
                      />
                      <span className="absolute -left-px -top-px h-5 w-5 border-l-2 border-t-2 border-[#F5B301]" />
                      <span className="absolute -bottom-px -right-px h-5 w-5 border-b-2 border-r-2 border-[#F5B301]" />
                    </div>
                    {(current?.title || current?.description) && (
                      <p className="mt-3 font-tac text-xs leading-relaxed text-white/60">
                        {current.title && <span className="text-[#F5B301]">{current.title}. </span>}
                        {current.description}
                      </p>
                    )}
                    {shots.length > 1 && (
                      <div className="mt-4 flex flex-wrap gap-3">
                        {shots.map((s, i) => (
                          <button
                            key={s.id}
                            type="button"
                            data-testid={`connect-screenshot-thumb-${i}`}
                            onClick={() => setActive(i)}
                            className={`h-16 w-24 shrink-0 overflow-hidden border transition-colors ${
                              i === active ? "border-[#F5B301]" : "border-white/15 hover:border-white/40"
                            }`}
                          >
                            <img src={`${BACKEND}${s.url}`} alt={s.alt || s.title || ""} className="h-full w-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    data-testid="connect-screenshots-empty"
                    className="mt-4 flex aspect-video flex-col items-center justify-center gap-3 border border-dashed border-white/15 bg-white/[0.02] px-6 text-center"
                  >
                    <ImageOff className="h-8 w-8 text-white/25" strokeWidth={1.5} />
                    <p className="font-tac text-xs leading-relaxed text-white/40">
                      App-Einblicke folgen in Kürze.
                    </p>
                  </div>
                )}
              </div>
            </Reveal>

            {/* FIELD-REPORT-11 highlight */}
            <Reveal delay={0.15}>
              <div
                data-testid="connect-fieldreport-block"
                className="mt-8 flex items-start gap-4 border border-[#F5B301]/30 bg-[#F5B301]/[0.04] p-6"
              >
                <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-[#F5B301]" strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-xl font-black uppercase tracking-tight text-white">
                    Field-Report-11
                  </h3>
                  <p className="mt-2 max-w-xl font-tac text-xs leading-relaxed text-white/70">
                    Das Dokumentationsmodul von DROPS Connect: Fotos, Flugdaten und Befunde werden strukturiert
                    erfasst und automatisch zu einem einheitlichen, revisionssicheren Bericht zusammengeführt —
                    als belastbare Grundlage für die fachliche Bewertung durch Ihre Ingenieure.
                  </p>
                </div>
              </div>
            </Reveal>

            {/* CTA */}
            <Reveal delay={0.2}>
              <button
                type="button"
                data-testid="connect-kunden-login-button"
                onClick={portalNotice}
                className="group relative mt-8 inline-flex items-center gap-2 overflow-hidden bg-[#F5B301] px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-black transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
              >
                <span className="absolute inset-0 -translate-x-full bg-black/15 transition-transform duration-300 group-hover:translate-x-0" />
                <LogIn className="relative z-10 h-4 w-4" />
                <span className="relative z-10">KUNDEN-LOGIN</span>
                <ArrowUpRight className="relative z-10 h-4 w-4" />
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
